import { makeAutoObservable } from 'mobx';
import { DictionaryModule } from '@mobx/services/dictionaries/model/DictionaryModule';
import { AccountType, Bank } from '@shared/types/dictionaries';
import { AccountForm } from '@shared/types/osi/accounts';
import { notifications } from '@shared/utils/notifications';
import { inject, injectable } from 'inversify';
import { EditAccountButtonViewModelInterface } from '@shared/types/mobx/features/osiAccounts';
import { OsiAccount } from '@shared/types/osi';
import { ApplicationTypes, OsiAccountApplicationCheckRequest } from '@shared/types/osiAccountApplications';
import { checkIsHasActiveOsiAccountApplication } from '@shared/api/osiAccountApplications';
import logger from 'js-logger';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';

const CLOSE_ANIMATION_TRANSITION_DELAY = 200;

@injectable()
export class EditAccountButtonFeature implements EditAccountButtonViewModelInterface {
  isFeatureDisabled = false;

  isDialogOpen = false;

  hookForm: any = null;

  defaultValues: AccountForm = {
    account: '',
    accountType: '',
    bank: {
      bic: '',
      name: ''
    }
  };

  constructor(private dictionary: DictionaryModule, @inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);
  }

  get accountTypes(): AccountType[] {
    return this.dictionary.accountTypes;
  }

  get banks(): Bank[] {
    return this.dictionary.banks;
  }

  editAccountCb = (data: AccountForm) => {};

  setEditAccountCb(cb: (data: AccountForm) => void): void {
    this.editAccountCb = cb;
  }

  setForm = (hookForm: any): void => {
    this.hookForm = hookForm;
  };

  onChangeAccount = (value: string): void => {
    // OSI-806: единая нормализация — в форму (а значит и в валидатор checkIban, и на бэк)
    // уходит uppercase без пробелов. Валидатор строгий и сам ничего не нормализует.
    const normalized = value.toUpperCase().replace(/\s/g, '');
    this.hookForm.setValue('account', normalized);
    this.hookForm.trigger('account');
    if (normalized.length < 7) return;

    const bicId = normalized.slice(4, 7);
    const bank = this.banks.find((bank) => bank.identifier === bicId);

    if (!bank) return;

    this.hookForm.setValue('bank', {
      bic: bank.bic,
      name: bank.name
    });
    this.hookForm.trigger();
  };

  onEditAccountClick = async (account: OsiAccount) => {
    if (this.isFeatureDisabled) {
      notifications.warning('Для добавления счета обратитесь к оператору');
      return;
    }

    if (!account.id) return;

    try {
      await this.checkAccountApplications(account);

      const editValues: AccountForm & { id: number } = {
        account: account.account,
        accountType: account.type,
        bank: {
          bic: account.bic,
          name: account.bankName || ''
        },
        id: account.id
      };

      this.isDialogOpen = true;
      this.hookForm.reset(editValues);
      this.hookForm.trigger();
    } catch (e) {
      logger.error(e);
      notifications.error(e);
    }
  };

  checkAccountApplications = async (account: OsiAccount) => {
    if (!this.osiModule.osiId) return false;

    const payload: OsiAccountApplicationCheckRequest = {
      osiId: this.osiModule.osiId,
      osiAccountId: account.id,
      type: account.type,
      applicationType: ApplicationTypes.UPDATE
    };
    await checkIsHasActiveOsiAccountApplication(payload);
    return true;
  };

  onCloseDialogClick = () => {
    this.isDialogOpen = false;

    setTimeout(() => {
      this.hookForm.reset(this.defaultValues);
      this.hookForm.trigger();
    }, CLOSE_ANIMATION_TRANSITION_DELAY);
  };

  onSaveAccountClick = async () => {
    await this.hookForm.handleSubmit(this.editAccountCb)();
    this.onCloseDialogClick();
  };
}
